import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "../App.css";
import heroImage from "../assets/images/heroimage.jpg";
import servicesImage from "../assets/images/servicesImage.jpg";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CheckLoginRedirect from "../components/CheckLoginRedirect";
import {
  Zap,
  Smartphone,
  Tv,
  Wifi,
  Shield,
  ThumbsUp,
  Clock,
} from "lucide-react";
import avatarOne from "../assets/icons/avatar1.svg";
import avatarTwo from "../assets/icons/avatar2.svg";
import avatarThree from "../assets/icons/avatar3.svg";
import avatarFour from "../assets/icons/avatar4.svg";

function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div className="overflow-x-hidden bg-white min-h-screen">
      <CheckLoginRedirect />
      <Header />

      {/* SECTION 1 - HERO */}
      <div className="w-4/5 mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center py-28">
        {/* LEFT CONTENT */}
        <div data-aos="fade-right" className="space-y-8">
          <h1 className="text-4xl sm:text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
            The <span className="text-[#007AFF] ">Smartest Way</span> <br />
            to Verify Identities <br />
            and Protect Your Brand
          </h1>

          <p className="text-gray-700 text-m sm:text-base md:text-lg leading-relaxed max-w-lg">
            Stop losing trust with your customers. Dotti Verify helps you
            confirm real users instantly, safeguard your business from fraud,
            and give your clients peace of mind.
          </p>

          {/* Features */}
          <div className="flex flex-wrap gap-6 text-gray-800 text-base font-semibold">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#007AFF]"></span>
              Bank-Level Security
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#007AFF]"></span>
              Instant Verifications
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#007AFF]"></span>
              Trusted Worldwide
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-5 flex-wrap">
            <button
              onClick={() => navigate("/signup")}
              className="bg-[#007AFF] text-white 
               px-4 py-2 text-sm 
               sm:px-6 sm:py-3 sm:text-base 
               md:px-8 md:py-4 md:text-lg 
               rounded-lg shadow hover:bg-blue-600 transition"
            >
              Get Started
            </button>

            <button
              onClick={() => navigate("/About")}
              className="border border-gray-300 text-gray-700 
               px-4 py-2 text-sm 
               sm:px-6 sm:py-3 sm:text-base 
               md:px-8 md:py-4 md:text-lg 
               rounded-lg hover:bg-gray-100 transition"
            >
              Learn More
            </button>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div data-aos="fade-left" className="flex justify-center relative">
          <img
            src={heroImage}
            alt="Hero Section"
            className="
      rounded-2xl shadow-xl 
      w-full h-40       /* Mobile: shorter height */
      sm:h-64           /* Small screens */
      md:h-[500px]      /* Medium screens */
      lg:h-[600px]      /* Large screens */
      object-cover
    "
          />

          <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-white px-3 sm:px-5 py-1.5 sm:py-2 rounded-full shadow text-xs sm:text-base text-gray-700 font-medium">
            <span className="inline-block w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500 mr-1 sm:mr-2"></span>
            15,000+ Verified Users
          </div>
        </div>
      </div>

      {/* SECTION 2 - HOW IT WORKS */}
      <div className="bg-gray-50 py-8 sm:py-16 md:py-24">
        <div className="w-4/5 mx-auto text-center" data-aos="fade-up">
          <h2 className="text-2xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-2">
            Get Started with{" "}
            <span className="text-[#007AFF]">Dotti Verify</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-10">
            Verification made simple. Follow these quick steps and start
            receiving SMS codes instantly.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Step 1 */}
            <div
              className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="w-14 h-14 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                01
              </div>
              <h3 className="text-xl font-semibold mb-4">Sign Up</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-m">
                Create your free account in minutes and unlock access to your
                personal dashboard.
              </p>
            </div>

            {/* Step 2 */}
            <div
              className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="w-14 h-14 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                02
              </div>
              <h3 className="text-xl font-semibold mb-4">Top Up Balance</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-m">
                Add funds quickly and securely using your preferred payment
                method.
              </p>
            </div>

            {/* Step 3 */}
            <div
              className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="w-14 h-14 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                03
              </div>
              <h3 className="text-xl font-semibold mb-4">Choose a Number</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-m">
                Select from our wide range of international virtual numbers
                available instantly.
              </p>
            </div>

            {/* Step 4 */}
            <div
              className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="w-14 h-14 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                04
              </div>
              <h3 className="text-xl font-semibold mb-4">Verify & Manage</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-m">
                Instantly receive SMS verification codes and manage all your
                activities from one place.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3 - SERVICES */}

      <div className="bg-gray-50 py-24">
        <div className="w-4/5 mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div data-aos="fade-right">
            <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#007AFF]">Services</span>
            </h2>
            <p className="text-sm sm:text-base md:text-m text-gray-700 mb-12 max-w-lg">
              Fast, reliable, and secure bill payments and top-ups — all in one
              place. Whether it's electricity, airtime, cable, or internet,
              we’ve got you covered.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Electricity Bills */}
              <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
                <div className="w-12 h-12 bg-[#007AFF] text-white rounded-full flex items-center justify-center mb-4">
                  <Zap size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Electricity Bills
                </h3>
                <p className="text-sm sm:text-base md:text-m text-gray-600">
                  Pay PHCN, EEDC, EKEDC & more with instant processing, no
                  hidden fees, 24/7 availability.
                </p>
              </div>

              {/* Airtime & Data */}
              <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
                <div className="w-12 h-12 bg-[#007AFF] text-white rounded-full flex items-center justify-center mb-4">
                  <Smartphone size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-2">Airtime & Data</h3>
                <p className="text-sm sm:text-base md:text-m text-gray-600">
                  Top up across all networks. Instant delivery with the best
                  rates, anytime.
                </p>
              </div>

              {/* Cable TV */}
              <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
                <div className="w-12 h-12 bg-[#007AFF] text-white rounded-full flex items-center justify-center mb-4">
                  <Tv size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-2">Cable TV</h3>
                <p className="text-sm sm:text-base md:text-m text-gray-600">
                  Renew DStv, GOtv & StarTimes with automatic renewal and no
                  service interruption.
                </p>
              </div>

              {/* Internet Bills */}
              <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
                <div className="w-12 h-12 bg-[#007AFF] text-white rounded-full flex items-center justify-center mb-4">
                  <Wifi size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-2">Internet Bills</h3>
                <p className="text-sm sm:text-base md:text-m text-gray-600">
                  Pay for all ISPs with quick processing and reliable service.
                </p>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div data-aos="fade-left" className="flex justify-center">
            <img
              src={servicesImage}
              alt="Services"
              className="rounded-2xl shadow-xl w-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <section className="bg-gray-50 py-4 sm:py-16 md:py-20">
        <div className="w-4/5 mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 mb-2">
            Why <span className="text-[#007AFF]">Choose Us</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-16">
            We provide reliable, fast, and secure services you can trust.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Item 1 */}
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Shield className="w-12 h-12 text-[#007AFF] mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-4">Secure</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg">
                Your transactions and data are always protected.
              </p>
            </div>

            {/* Item 2 */}
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Zap className="w-12 h-12 text-[#007AFF] mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-4">Fast</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg">
                Experience lightning-fast payments and verifications.
              </p>
            </div>

            {/* Item 3 */}
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <ThumbsUp className="w-12 h-12 text-[#007AFF] mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-4">Trusted</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg">
                Thousands of users trust us daily for their needs.
              </p>
            </div>

            {/* Item 4 */}
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Clock className="w-12 h-12 text-[#007AFF] mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-4">24/7 Support</h3>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg">
                We’re always available whenever you need us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="w-4/5 mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 mb-2">
            What Our <span className="text-[#007AFF]">Customers Say</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-5">
            Hear from real users who trust and use our services daily.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition flex flex-col items-center">
              <div className="flex justify-center mb-4">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">
                      ★
                    </span>
                  ))}
              </div>
              <p className="text-sm sm:text-base md:text-base text-gray-600 mb-4">
                “Super fast and easy to use. Highly recommend!”
              </p>
              <img
                src={avatarTwo}
                alt="Sarah A."
                className="w-14 h-14 rounded-full mb-2 object-cover"
              />
              <h4 className="font-semibold text-gray-900">Sarah A.</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-500">
                Lagos, Nigeria
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition flex flex-col items-center">
              <div className="flex justify-center mb-4">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">
                      ★
                    </span>
                  ))}
              </div>
              <p className="text-sm sm:text-base md:text-base text-gray-600 mb-4">
                “Reliable and trustworthy platform. Never had issues.”
              </p>
              <img
                src={avatarOne}
                alt="James K."
                className="w-14 h-14 rounded-full mb-2 object-cover"
              />
              <h4 className="font-semibold text-gray-900">James K.</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-500">
                Abuja, Nigeria
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition flex flex-col items-center">
              <div className="flex justify-center mb-4">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">
                      ★
                    </span>
                  ))}
              </div>
              <p className="text-sm sm:text-base md:text-base text-gray-600 mb-4">
                “The best rates I’ve seen so far. Very affordable.”
              </p>
              <img
                src={avatarFour}
                alt="Chioma O."
                className="w-14 h-14 rounded-full mb-2 object-cover"
              />
              <h4 className="font-semibold text-gray-900">Chioma O.</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-500">
                Port Harcourt
              </p>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition flex flex-col items-center">
              <div className="flex justify-center mb-4">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">
                      ★
                    </span>
                  ))}
              </div>
              <p className="text-sm sm:text-base md:text-base text-gray-600 mb-4">
                “Great support team. They helped me instantly.”
              </p>
              <img
                src={avatarThree}
                alt="Michael T."
                className="w-14 h-14 rounded-full mb-2 object-cover"
              />
              <h4 className="font-semibold text-gray-900">Michael T.</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-500">
                Ibadan, Nigeria
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="bg-[#007AFF] py-24 text-center">
        <div className="w-4/5 mx-auto">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Get in Touch With Us
          </h2>
          <p className="text-m sm:text-base md:text-2xl text-white/90 mb-10">
            Have questions or need support? We’re here to help you 24/7.
          </p>
          <a
            href="/contact"
            className="px-10 py-4 bg-white text-[#007AFF] text-xl font-semibold rounded-full shadow-lg hover:bg-gray-100 transition"
          >
            Contact Us
          </a>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gray-900 py-24 text-center text-white">
        <div className="w-4/5 mx-auto">
          <h2 className="text-4xl sm:text-4xl md:text-5xl font-bold mb-6">
            Ready to <span className="text-[#007AFF]">Get Started?</span>
          </h2>
          <p className="text-m sm:text-base md:text-2xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join thousands of users already using{" "}
            <span className="font-semibold text-white">Dotti Verify</span> to{" "}
            make their payments and verifications faster, easier, and safer.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="/signup"
              className="bg-[#007AFF] text-white font-semibold text-lg px-8 py-4 rounded-full shadow-lg hover:bg-blue-600 transition"
            >
              Get Started
            </a>
            <a
              href="/services"
              className="bg-white text-gray-900 font-semibold text-lg px-8 py-4 rounded-full shadow-lg hover:bg-gray-100 transition"
            >
              View Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
